(ns hai.notes.mini-app
  "The notes mini-app card shared by the home dashboard and the extension
   sidebar: the mini-app frame with the perforation shifted right and a header
   kebab restyled to match the round header buttons, the two headers the card
   wears — over the list, and over a note open in it — and the two bodies it
   swaps between, the note list and the editor pane a clicked row (or ＋)
   opens in its place. The card keeps the frame's neutral tray: the sticky
   yellow is the rows' and the editor paper's, not the card's, so notes never
   sit yellow-on-yellow. Hosts bring the card frame, the sizes, and the data
   wiring; everything a user sees comes from here."
  (:require
   [hai.entity.form :as entity-form]
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.mini-apps.add-pill :as add-pill]
   [hai.mini-apps.frame :as frame]
   [hai.notes.card :as note-card]
   [hai.notes.editor :as editor]
   [hai.notes.header :as header]
   [hai.notes.recent :as recent]
   [hai.sharing.helper :as sharing]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private expand-icon (icons/mui-icon :outlined :open_in_full))

(o/defstyled card frame/card
  [:&::before {:left "auto" :right "36px"}]
  [header/bar
   {:border-top-left-radius "16px"
    :border-top-right-radius "16px"}
   [sections/kebab-wrap
    [:button
     {:width "28px" :height "28px" :border-radius "999px"
      :color t/--note-kraft}
     [:&:hover {:background t/--note-kraft-10 :color t/--note-kraft}]]]]
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "16px"
     :border-bottom-right-radius "16px"}]])

;;
;; Headers
;;

(defn- action-button
  [{:keys [label on-click icon]}]
  [tt/tooltip {:content label}
   [:button.bar-action-btn
    {:type "button"
     :aria-label label
     :on-click on-click}
    icon]])

(defn list-header
  "The card's header over the list: the title as a link to the notes page
   (`href`, `target` and `rel` are the host's anchor attrs), a search button
   when `on-search` is given, the ＋ Note pill (`add-tooltip` says what it
   adds where) and the collapse toggle."
  [{:keys [title href target rel collapsed? on-toggle-collapsed on-search on-add add-tooltip]
    :or {add-tooltip "New note"}}]
  [header/bar {:title title :href href :target target :rel rel :dotted? (not collapsed?)}
   (when on-search
     [action-button {:label "Search notes"
                     :on-click on-search
                     :icon [icons/outlined-search {:size 18}]}])
   [add-pill/component
    {:label      "Note"
     :aria-label add-tooltip
     :tooltip    add-tooltip
     :on-add     on-add}]
   [frame/collapse-toggle
    {:collapsed? collapsed?
     :label      "notes"
     :class      "bar-action-btn"
     :on-toggle  on-toggle-collapsed}]])

(defn editor-title [note]
  (if (:note/uuid note) "Editing note" "Creating note"))

(defn editable?
  "Whether the editor opens `note` for writing: a draft always, a saved note
   when the requester may update it."
  [note]
  (or (nil? (:note/uuid note)) (sharing/can-edit? note)))

(defn editing-header
  "The card's header while a note is open in it: retitled, with the share
   button, the ⋮ when the note exists and may be deleted (`on-delete`), ⤢
   when the host has a roomier editor to offer (`on-expand`), and ✕."
  [{:keys [note on-close on-expand on-delete on-share-opened on-share-closed]}]
  [header/bar {:title (editor-title note) :dotted? true}
   [note-card/share-button {:note            note
                            :on-share-opened on-share-opened
                            :on-share-closed on-share-closed}]
   (when (and on-delete (:note/uuid note) (sharing/owner? note))
     [kebab/menu
      {:on-delete      on-delete
       :delete-label   "Delete note"
       :confirm-prompt "Delete this note?"
       :aria-label     "Note actions"}])
   (when on-expand
     [action-button {:label "Edit in full screen"
                     :on-click on-expand
                     :icon [expand-icon]}])
   [action-button {:label "Close"
                   :on-click on-close
                   :icon [icons/outlined-close {:size 18}]}]])

;;
;; The list
;;

;; The list's scroll column under the header: rows stacked in the card,
;; scrolling inside it instead of stretching it.
;; The scrollbar is styled so that it shows whenever the list overflows: an
;; unstyled one is the browser's, drawn or hidden at idle by the OS and even
;; by the tab. Standard props only — a scrollbar-color inherited from a host's
;; scroll column makes Chrome ignore ::-webkit-scrollbar rules. Two tokens in
;; one value can't be interpolated in a pure-cljs defstyled, so they're named
;; by hand.
(o/defstyled list-body :div
  {:display "flex" :flex-direction "column" :gap "10px"
   :padding "12px 14px 16px"
   :flex "1 1 auto" :min-height "0"
   :overflow-y "auto"
   :overflow-x "hidden"
   :scrollbar-width "thin"
   :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"}
  [:.empty
   {:padding "18px 4px 8px"
    :font-size "13px"
    :color t/--link-muted
    :text-align "center"
    :font-style "italic"}]
  [:.loading
   {:display "flex" :justify-content "center"
    :padding "18px 0"}]
  ;; List rows are uniform, shorter than the wall's 300px cell, and don't
  ;; scroll inside — overflowing text just fades at the fold, and a wheel over
  ;; a row scrolls the list. A click opens the editor.
  [note-card/card-surface
   {:height "140px" :flex "0 0 auto"}
   [:.card-scroll {:overflow-y "hidden"}]])

(defn list-loading []
  [list-body [:div.loading [icons/spinner {:size 20}]]])

(defn list-empty [text]
  [list-body [:div.empty text]])

;; Smooths the scroll cutoff: rows clipped by the scrollport dissolve into the
;; tray just above the card's rounded bottom edge. Positioned against the
;; card, so it sits beside the list body rather than inside its scrollport.
;; The transparent end restates --card-surface's value by hand (no str
;; interpolation in a pure cljs defstyled).
(o/defstyled bottom-fade :div
  {:position "absolute"
   :left "0" :right "0" :bottom "0"
   :height "26px"
   :background "linear-gradient(to bottom, rgba(247,246,239,0), var(--card-surface))"
   :border-bottom-left-radius "16px"
   :border-bottom-right-radius "16px"
   :pointer-events "none"
   :z-index "1"})

;; Under the last row: that the list is cut at the latest few, once it is,
;; and the way to the rest. The bottom padding keeps it clear of the fade
;; once the list is scrolled to its end.
(o/defstyled list-footer :div
  {:display "flex" :flex-direction "column" :align-items "center" :gap "4px"
   :padding "10px 4px 18px"
   :border-top-width "1px" :border-top-style "dashed" :border-top-color t/--note-sage-35
   :font-size "12px"
   :color t/--link-muted}
  [:.see-all-link
   {:color t/--note-kraft
    :font-weight "600"
    :text-decoration "underline"
    :text-underline-offset "3px"}
   [:&:hover {:color t/--card-forest}]]
  ([{:keys [notes see-all]}]
   [:<>
    (when (>= (count notes) recent/limit)
      [:span (str "Showing your latest " recent/limit " notes")])
    [:a.see-all-link see-all "See all notes"]]))

(defn note-row
  "One list row: the read-only card, its whole surface handing `on-open` the
   note."
  [note {:keys [on-open]}]
  [note-card/card
   {:note          note
    :tags          (vec (:note/tags note))
    :link-urls     (vec (keep :activity/url (:note/activities note)))
    :contacts      (vec (:note/contacts note))
    :linked-notes  (vec (:note/notes note))
    :linked-tasks  (vec (:note/tasks note))
    :footer?       false
    :on-body-click #(on-open note)}])

;; Rounds the scrollbar's end into the card's bottom corners. Chrome clips a
;; scrollbar by an ancestor's overflow clip, never by the scroller's own
;; border-radius; the sidebar clips its whole card, but on Home the frame's
;; duck-tape strip overhangs it, so the clip sits here, around the list alone.
;; The fade positions against this box, whose bottom is the card's.
(o/defstyled list-clip :div
  {:position "relative"
   :display "flex" :flex-direction "column"
   :flex "1 1 auto" :min-height "0"
   :overflow "hidden"
   :border-bottom-left-radius "16px"
   :border-bottom-right-radius "16px"})

(defn note-list
  "The single-group list: the rows as given — or `empty-text` — with the
   footer under them and the fade beside the body. `see-all` is the footer
   link's anchor attrs; the host owns the URL shape."
  [{:keys [notes on-open see-all empty-text]}]
  (if (empty? notes)
    [list-empty empty-text]
    [list-clip
     ;; The rows go straight into the body, not as one leading `[:<>]` child:
     ;; Ornament's fragment-flattening recurses into a leading fragment and
     ;; drops the siblings after it — the footer, here.
     (-> [list-body]
         (into (for [note notes]
                 ^{:key (:note/uuid note)}
                 [note-row note {:on-open on-open}]))
         (conj [list-footer {:notes notes :see-all see-all}]))
     [bottom-fade]]))

;;
;; The editor
;;

;; The editor in the card's body: the shared paper under the tinted header,
;; where the textarea flexes and scrolls and the chip sections and footer keep
;; their natural height below it.
(o/defstyled editor-pane editor/paper
  {:border-bottom-left-radius "16px"
   :border-bottom-right-radius "16px"
   :display "flex" :flex-direction "column" :gap "14px"
   :padding "12px 14px 16px"
   :flex "1 1 auto" :min-height "0"}
  [:textarea.body
   {:width "100%" :box-sizing "border-box"
    :background "transparent" :border "none" :outline "none" :resize "none"
    :padding "0" :margin "0"
    :font-family "inherit" :font-size "14px" :font-weight "500" :line-height "1.5"
    :color t/--charcoal
    :flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :overscroll-behavior "contain"}
   [:&::placeholder {:color t/--note-sage-55}]]
  [#{sections/extras-wrap sections/add-section-row entity-form/footer
     note-card/note-footer-styled}
   {:flex "0 0 auto"}])

(defn editor-view
  "The pane with the editor innards on `note` ([[hai.notes.editor/body]]'s
   props), read-only for a note the requester may not update. `pane-ref`
   hands the host the pane element, to put the focus back in the text after
   a detour through a roomier editor."
  [{:keys [note pane-ref] :as props}]
  [editor-pane {:ref pane-ref}
   [editor/body (-> props
                    (dissoc :pane-ref)
                    (assoc :editing? (editable? note)
                           :share? (sharing/can-share? note)))]])
